import { useState, useCallback, useEffect, useRef } from "react";
import type { PlaceImage } from "~/lib/api";
import { cn } from "~/lib/utils";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "~/components/ui/carousel";
import { ScrollingDots } from "~/components/ui/scrolling-dots";

/** Minimum movement (px) before locking drag direction */
const DRAG_DIRECTION_THRESHOLD = 5;

interface ImageGalleryProps {
  images: PlaceImage[];
  placeName: string;
  /** Called when user clicks on the gallery, with current image index */
  onClick?: (currentIndex: number) => void;
  className?: string;
}

/** Full-width hero carousel for place images */
export function ImageGallery({ images, placeName, onClick, className }: ImageGalleryProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  // Track drag direction to prevent carousel from hijacking vertical drags
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const dragDirection = useRef<"horizontal" | "vertical" | null>(null);

  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api, onSelect]);

  // Embla watchDrag callback - return false to prevent carousel from handling drag
  const watchDrag = useCallback((_api: CarouselApi, event: MouseEvent | TouchEvent) => {
    const point = "touches" in event ? event.touches[0] : event;
    dragStart.current = { x: point.clientX, y: point.clientY };
    dragDirection.current = null;
    return true; // Allow drag to start, we'll cancel if vertical
  }, []);

  // Check drag direction on pointer/touch move
  const handleDragMove = useCallback(
    (e: React.PointerEvent | React.TouchEvent) => {
      if (!dragStart.current) return;

      // Determine direction once
      if (!dragDirection.current) {
        const point = "touches" in e ? e.touches[0] : e;
        const dx = Math.abs(point.clientX - dragStart.current.x);
        const dy = Math.abs(point.clientY - dragStart.current.y);

        if (dx > DRAG_DIRECTION_THRESHOLD || dy > DRAG_DIRECTION_THRESHOLD) {
          dragDirection.current = dx > dy ? "horizontal" : "vertical";
          if (dragDirection.current === "vertical") {
            // Cancel carousel drag by re-initializing
            api?.reInit();
          }
        }
      }

      // Stop propagation for horizontal drags to prevent bottom sheet from responding
      if (dragDirection.current === "horizontal") {
        e.stopPropagation();
      }
    },
    [api],
  );

  const handleDragEnd = useCallback((e: React.UIEvent) => {
    if (dragDirection.current === "horizontal") {
      e.stopPropagation();
    }
    dragStart.current = null;
    dragDirection.current = null;
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && onClick) {
      onClick(current);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      api?.scrollPrev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      api?.scrollNext();
    }
  };

  if (images.length === 0) return null;

  const showDots = images.length > 1;

  return (
    <div
      className={cn("relative overflow-hidden touch-pan-x", { "cursor-pointer": onClick }, className)}
      onClick={() => onClick?.(current)}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={handleKeyDown}
      // Handle both pointer and touch events - some browsers don't bubble touch to pointer
      onPointerMove={handleDragMove}
      onPointerUp={handleDragEnd}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
    >
      <Carousel setApi={setApi} opts={{ loop: true, watchDrag }} className="h-full w-full">
        <CarouselContent className="ml-0 h-full">
          {images.map((image, index) => (
            <CarouselItem key={image.id} className="pl-0 h-full">
              <div
                className="h-full w-full bg-cover bg-center"
                style={{ backgroundImage: `url(${image.medium})` }}
                role="img"
                aria-label={`Photo ${index + 1} of ${placeName}`}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {showDots && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex px-2 py-1 rounded-full bg-black/20 backdrop-blur-sm">
          <ScrollingDots total={images.length} current={current} onSelect={(i) => api?.scrollTo(i)} />
        </div>
      )}
    </div>
  );
}
